import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'

interface LogoProps {
  size?: number
  className?: string
  variant?: 'mark' | 'full' | 'lockup'
  animated?: boolean
  glow?: boolean
}

// Nouvelle image officielle — Asso Centre de Transformation
const LOGO_URL = 'https://static.lumi.new/14/14db1b66f5e3ae8829f9c52f5d24b2ab.webp'

/**
 * Logo — Asso Centre de Transformation
 * - mark   : emblème circulaire seul (halo doré + anneau premium)
 * - full   : image officielle complète (pour Hero ou Footer grand format)
 * - lockup : emblème + wordmark "CENTRE DE / Transformation" (style Nav)
 */
const Logo: React.FC<LogoProps> = ({
  size = 56,
  className = '',
  variant = 'mark',
  animated = true,
  glow = true,
}) => {
  const reduceMotion = useReducedMotion()

  // ─── Variant: full ────────────────────────────────────────────────────
  if (variant === 'full') {
    return (
      <motion.img
        src={LOGO_URL}
        alt="Asso Centre de Transformation"
        style={{ height: size, width: 'auto', objectFit: 'contain' }}
        className={className}
        loading="eager"
        decoding="async"
        initial={animated && !reduceMotion ? { opacity: 0, y: 6 } : false}
        animate={animated && !reduceMotion ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      />
    )
  }

  // ─── Emblème circulaire (partagé par mark + lockup) ───────────────────
  const Mark = (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-label="Asso Centre de Transformation"
      role="img"
    >
      {/* Halo doré pulsant */}
      {glow && (
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, rgba(200,162,74,0.28) 0%, rgba(180,210,80,0.10) 40%, transparent 70%)',
            filter: 'blur(10px)',
            transform: 'scale(1.5)',
            zIndex: 0,
          }}
          initial={animated && !reduceMotion ? { opacity: 0 } : false}
          animate={
            animated && !reduceMotion
              ? { opacity: [0.5, 0.9, 0.5] }
              : { opacity: 0.65 }
          }
          transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* Fond circulaire navy profond */}
      <span
        className="block absolute inset-0 rounded-full overflow-hidden"
        style={{
          background: 'radial-gradient(circle at 50% 40%, #162B44 0%, #0B1928 100%)',
          zIndex: 1,
        }}
      >
        {/* Image emblème — centrée et légèrement zoomée */}
        <img
          src={LOGO_URL}
          alt=""
          aria-hidden="true"
          style={{
            width: size * 1.55,
            height: 'auto',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'block',
            objectFit: 'contain',
          }}
          loading="eager"
          decoding="async"
        />

        {/* Voile périmétrique pour fondre les bords */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, transparent 52%, rgba(11,25,40,0.55) 100%)',
          }}
        />
      </span>

      {/* Anneau extérieur or fin — sertissage premium */}
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          boxShadow:
            '0 0 0 1.5px rgba(200,162,74,0.65), inset 0 0 0 1px rgba(232,220,196,0.08)',
          zIndex: 2,
        }}
      />

      {/* Reflet de lumière subtil en haut */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            'linear-gradient(165deg, rgba(232,220,196,0.15) 0%, transparent 40%)',
          mixBlendMode: 'overlay',
          zIndex: 3,
        }}
      />
    </div>
  )

  // ─── Variant: mark ────────────────────────────────────────────────────
  if (variant === 'mark') {
    return (
      <motion.span
        className={`inline-block ${className}`}
        initial={animated && !reduceMotion ? { opacity: 0, scale: 0.88 } : false}
        animate={animated && !reduceMotion ? { opacity: 1, scale: 1 } : undefined}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        whileHover={!reduceMotion ? { scale: 1.05 } : undefined}
      >
        {Mark}
      </motion.span>
    )
  }

  // ─── Variant: lockup (emblème + wordmark — fidèle au screenshot Nav) ──
  return (
    <motion.span
      className={`inline-flex items-center gap-3 ${className}`}
      initial={animated && !reduceMotion ? { opacity: 0, x: -10 } : false}
      animate={animated && !reduceMotion ? { opacity: 1, x: 0 } : undefined}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      {Mark}

      {/* Wordmark : "CENTRE DE" + "Transformation" */}
      <span className="flex flex-col leading-none select-none">
        <span
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: size * 0.195,
            letterSpacing: '0.26em',
            textTransform: 'uppercase',
            color: '#C8A24A',
            fontWeight: 500,
            lineHeight: 1,
          }}
        >
          Centre de
        </span>
        <span
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: size * 0.38,
            letterSpacing: '0.01em',
            color: '#E8DCC4',
            fontWeight: 400,
            lineHeight: 1.15,
            marginTop: size * 0.06,
          }}
        >
          Transformation
        </span>
      </span>
    </motion.span>
  )
}

export default Logo
